import {
  ConditionGroupLogic,
  isConditionGroup,
  isRelatedResourceCondition,
  type ConditionGroup,
  type ConditionGroupItem,
  type FieldCondition,
  type RelatedResourceCondition,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import { generateId } from '../utils/generate-id';
import { findRelationshipForStoredCondition } from '@reach/shared-types/segment-schema-graph';
import type { FieldInfo, SegmentSchemaCatalog } from '@reach/shared-types/segment-schema-graph';
import type { BuilderFieldType } from './segment-builder.types';
import {
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
  type BuilderCondition,
  type BuilderFieldCondition,
  type BuilderGroup,
  type BuilderNestedGroup,
  type BuilderRelatedResourceCondition,
  type BuilderState,
} from './segment-builder.types';

export type BuilderStateConversionResult =
  | { kind: 'supported'; state: BuilderState }
  | { kind: 'unsupported'; reason: string };
/** Spread a description key when it is present, including an explicitly empty description. */
function withDescription(description?: string): { description?: string } {
  return description === undefined ? {} : { description };
}

function fieldTypeFromFields(fieldName: string, fields: FieldInfo[]): BuilderFieldType {
  const info = fields.find(field => field.name === fieldName);
  return info?.type ?? 'unknown';
}

/**
 * Recursively convert a condition group item (API format) to a builder condition.
 */
function conditionItemToBuilder(
  item: ConditionGroupItem,
  schemaCatalog: SegmentSchemaCatalog,
  subjectSchemaId: string | null,
): BuilderCondition {
  if (isConditionGroup(item)) {
    const nestedConditions = item.conditions.map(child =>
      conditionItemToBuilder(child, schemaCatalog, subjectSchemaId),
    );
    return {
      id: generateId(),
      type: 'nested_group',
      logic: item.logic === ConditionGroupLogic.OR ? 'or' : 'and',
      conditions: nestedConditions,
      isExpanded: true,
      ...withDescription(item.description),
    } satisfies BuilderNestedGroup;
  }

  if (isRelatedResourceCondition(item)) {
    return relatedConditionToBuilder(item, schemaCatalog, subjectSchemaId);
  }

  const fieldCond = item as FieldCondition;
  const fieldName = typeof fieldCond.field === 'string' ? fieldCond.field : fieldCond.field;
  return {
    id: generateId(),
    type: 'field',
    field: fieldName,
    fieldType: fieldTypeFromFields(fieldName, schemaCatalog.contactFields),
    operator: fieldCond.operator,
    value: [...fieldCond.value],
    ...withDescription(fieldCond.description),
  } satisfies BuilderFieldCondition;
}

/**
 * Convert a stored related-resource condition to builder state, recursing into
 * nested related-resource children (relationship hops).
 */
function relatedConditionToBuilder(
  item: RelatedResourceCondition,
  schemaCatalog: SegmentSchemaCatalog,
  subjectSchemaId: string | null,
): BuilderRelatedResourceCondition {
  const schema = schemaCatalog.schemas.find(s => s.id === item.relatedResource.schemaId);
  const relationship = subjectSchemaId
    ? findRelationshipForStoredCondition({
        graph: schemaCatalog,
        subjectSchemaId,
        storedRelatedResource: item.relatedResource,
      })
    : undefined;
  const builderNested = item.conditions.map(
    (child): BuilderFieldCondition | BuilderRelatedResourceCondition => {
      if (isRelatedResourceCondition(child)) {
        return relatedConditionToBuilder(child, schemaCatalog, item.relatedResource.schemaId);
      }
      return {
        id: generateId(),
        type: 'field',
        field: child.field,
        fieldType: fieldTypeFromFields(child.field, schema?.fields ?? []),
        operator: child.operator,
        value: [...child.value],
        ...withDescription(child.description),
      };
    },
  );
  return {
    id: generateId(),
    type: 'related_resource',
    schemaId: item.relatedResource.schemaId,
    schemaName: schema?.name ?? 'Related resource',
    foreignKeyPath: item.relatedResource.foreignKeyPath,
    ...(item.relatedResource.foreignKeyOn ? { foreignKeyOn: item.relatedResource.foreignKeyOn } : {}),
    ...(subjectSchemaId !== null && !relationship
      ? { unavailableReason: 'This relationship is no longer available.' }
      : {}),
    countOperator: item.countOperator,
    countValue: item.countValue,
    conditions: builderNested,
    isExpanded: true,
    ...withDescription(item.description),
  };
}

/**
 * Convert segment condition groups (API format) to builder state.
 * Fully supports nested condition groups via recursion.
 */
export function conditionGroupsToBuilderState(
  groups: SegmentConditionGroups,
  schemaCatalog: SegmentSchemaCatalog,
): BuilderStateConversionResult {
  const builderGroups: BuilderGroup[] = groups.map((group: ConditionGroup) => {
    const conditions = group.conditions.map(cond =>
      conditionItemToBuilder(cond, schemaCatalog, schemaCatalog.contactSchemaId),
    );
    return {
      id: generateId(),
      logic: group.logic === ConditionGroupLogic.OR ? 'or' : 'and',
      conditions,
      isExpanded: true,
      ...withDescription(group.description),
    };
  });

  return { kind: 'supported', state: { groups: builderGroups } };
}

/**
 * Recursively convert a builder condition to API format.
 */
function builderConditionToApi(cond: BuilderCondition): ConditionGroupItem {
  if (isBuilderNestedGroup(cond)) {
    const nestedConditions = cond.conditions.map(builderConditionToApi);
    return {
      type: 'group',
      logic: cond.logic === 'or' ? ConditionGroupLogic.OR : ConditionGroupLogic.AND,
      conditions: nestedConditions,
      ...withDescription(cond.description),
    } as ConditionGroup;
  }

  if (isBuilderRelatedResourceCondition(cond)) {
    return builderRelatedConditionToApi(cond);
  }

  return {
    type: 'property',
    field: cond.field,
    operator: cond.operator,
    value: [...cond.value],
    ...withDescription(cond.description),
  } as FieldCondition;
}

/**
 * Convert a builder related-resource condition to API format, recursing into
 * nested related-resource children.
 */
function builderRelatedConditionToApi(cond: BuilderRelatedResourceCondition): RelatedResourceCondition {
  return {
    type: 'related_resource',
    relatedResource: {
      schemaId: cond.schemaId,
      foreignKeyPath: cond.foreignKeyPath,
      ...(cond.foreignKeyOn ? { foreignKeyOn: cond.foreignKeyOn } : {}),
    },
    countOperator: cond.countOperator,
    countValue: cond.countValue,
    conditions: cond.conditions.map(child => {
      if (isBuilderRelatedResourceCondition(child)) {
        return builderRelatedConditionToApi(child);
      }
      return {
        type: 'property' as const,
        field: child.field,
        operator: child.operator,
        value: [...child.value],
        ...withDescription(child.description),
      };
    }),
    ...withDescription(cond.description),
  };
}

/**
 * Convert builder state to segment condition groups (API format).
 * Fully supports nested condition groups via recursion.
 */
export function builderStateToConditionGroups(state: BuilderState): SegmentConditionGroups {
  return state.groups.map(group => {
    const conditions = group.conditions.map(builderConditionToApi);
    return {
      type: 'group' as const,
      logic: group.logic === 'or' ? ConditionGroupLogic.OR : ConditionGroupLogic.AND,
      conditions,
      ...withDescription(group.description),
    };
  });
}

/** Title-cases only each word's first letter, so partner-authored lowercase names read as labels and acronyms survive. */
export function titleCaseSchemaName(schemaName: string): string {
  return schemaName.replace(/\S+/g, word => `${word[0]?.toUpperCase() ?? ''}${word.slice(1)}`);
}

export function buildAddAttributeLabel(schemaName: string): string {
  return `${titleCaseSchemaName(schemaName)} Attribute`;
}
